import React, { useEffect, useState, useCallback } from 'react';
import { usePage } from '../../hooks/usePage';
import { adminsApi, CreateAdminDto, CreateAdminResponse, ResetAdminLoginResponse } from '../../api/admins';
import { rolesApi } from '../../api/roles';
import { AdminUser, AdminRole, AdminRoleSummary } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';

const roleColors: Record<AdminRole, 'danger' | 'warning' | 'info' | 'neutral'> = {
  super_admin: 'danger',
  operations: 'warning',
  finance: 'info',
  support: 'neutral',
};

const formatRole = (role: string) =>
  role.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const selectStyle: React.CSSProperties = {
  height: 40,
  padding: '0 32px 0 12px',
  fontSize: 14,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-800)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  appearance: 'none',
  backgroundImage:
    'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\' fill=\'none\'%3E%3Cpath d=\'M3 4.5L6 7.5L9 4.5\' stroke=\'%23566472\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  cursor: 'pointer',
  width: '100%',
};

type PendingAction =
  | { type: 'deactivate'; admin: AdminUser }
  | { type: 'reactivate'; admin: AdminUser }
  | null;

export const Admins: React.FC = () => {
  usePage('Admin Management');
  const { admin: currentAdmin } = useAuth();
  const toast = useToast();

  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<AdminRoleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdAdmin, setCreatedAdmin] = useState<CreateAdminResponse | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [resetCredentials, setResetCredentials] = useState<ResetAdminLoginResponse | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [reassigning, setReassigning] = useState<string | null>(null);

  const [form, setForm] = useState<CreateAdminDto>({
    email: '',
    firstName: '',
    lastName: '',
    roleId: '',
  });
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const [adminResult, roleResult] = await Promise.all([
        adminsApi.getAdmins(),
        rolesApi.getRoles(),
      ]);
      setAdmins(adminResult ?? []);
      setTotal(adminResult?.length ?? 0);
      setRoles(roleResult ?? []);
    } catch (err) {
      toast.error(err, 'Failed to load admins.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchAdmins();
  }, [fetchAdmins]);

  const handleReassign = async (admin: AdminUser, roleId: string) => {
    if (!roleId || roleId === admin.roleId) return;
    try {
      setReassigning(admin.id);
      await adminsApi.updateAdmin(admin.id, { roleId });
      toast.success(`${admin.firstName}'s role updated.`);
      void fetchAdmins();
    } catch (err) {
      toast.error(err, 'Could not update role.');
    } finally {
      setReassigning(null);
    }
  };

  const openCreate = () => {
    const defaultRole =
      roles.find((r) => r.key === 'support') ?? roles[0];
    setForm({
      email: '',
      firstName: '',
      lastName: '',
      roleId: defaultRole?.id ?? '',
    });
    setProfilePreview(null);
    setShowCreateModal(true);
  };

  const canResetAdminLogin = currentAdmin?.role === 'super_admin' || currentAdmin?.roleKey === 'super_admin';

  const handleCreate = async () => {
    if (!form.email || !form.firstName || !form.lastName) {
      toast.warning('Please fill in all fields.');
      return;
    }
    if (!form.roleId) {
      toast.warning('Select a role.');
      return;
    }
    try {
      setFormLoading(true);
      const result = await adminsApi.createAdmin(form);
      setShowCreateModal(false);
      setProfilePreview(null);
      setCreatedAdmin(result);
      void fetchAdmins();
    } catch (err) {
      toast.error(err, 'Failed to create admin.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleProfilePhotoChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.warning('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.warning('Profile picture must be under 5MB.');
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      setProfilePreview(base64);
      setForm((state) => ({ ...state, profilePhotoBase64: base64 }));
    } catch {
      toast.warning('Could not read the selected image.');
    }
  };

  const handleResetLogin = async () => {
    if (!resetTarget) return;
    try {
      setResetLoading(true);
      const result = await adminsApi.resetLogin(resetTarget.id);
      setResetTarget(null);
      setResetCredentials(result);
      toast.success(`Login reset for ${resetTarget.email}.`);
      void fetchAdmins();
    } catch (err) {
      toast.error(err, 'Could not reset admin login.');
    } finally {
      setResetLoading(false);
    }
  };

  const executeAction = async () => {
    if (!pendingAction) return;
    try {
      setActionLoading(true);
      if (pendingAction.type === 'deactivate') {
        await adminsApi.deactivateAdmin(pendingAction.admin.id);
        toast.success(`${pendingAction.admin.email} has been deactivated.`);
      } else {
        await adminsApi.reactivateAdmin(pendingAction.admin.id);
        toast.success(`${pendingAction.admin.email} has been reactivated.`);
      }
      setPendingAction(null);
      void fetchAdmins();
    } catch (err) {
      toast.error(err, 'Action failed.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
            {total} admin account{total !== 1 ? 's' : ''}
          </p>
          <Button size="sm" onClick={openCreate}>
            + Add Admin
          </Button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--neutral-200)' }}>
                  {['Name', 'Email', 'Role', 'Status', 'Last Login', ''].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--neutral-600)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        borderBottom: '1px solid var(--neutral-300)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {admins.map((a, i) => (
                  <tr
                    key={a.id}
                    style={{
                      background: i % 2 === 0 ? 'var(--neutral-100)' : 'var(--neutral-200)',
                      borderBottom: '1px solid var(--neutral-300)',
                    }}
                  >
                    <td style={{ padding: '14px 16px', fontWeight: 600, color: 'var(--neutral-800)' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: 'var(--blue-600)',
                            color: 'white',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 700,
                            overflow: 'hidden',
                          }}
                        >
                          {a.profilePhoto ? (
                            <img
                              src={a.profilePhoto}
                              alt=""
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            `${a.firstName[0] ?? ''}${a.lastName[0] ?? ''}`.toUpperCase()
                          )}
                        </span>
                        <span>
                          {a.firstName} {a.lastName}
                        </span>
                      </span>
                      {a.id === currentAdmin?.id && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            color: 'var(--blue-600)',
                            fontWeight: 400,
                          }}
                        >
                          (you)
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', color: 'var(--neutral-600)' }}>
                      {a.email}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      {a.id === currentAdmin?.id || roles.length === 0 ? (
                        <Badge variant={roleColors[a.role] ?? 'neutral'}>
                          {a.roleName ?? formatRole(a.role)}
                        </Badge>
                      ) : (
                        <select
                          value={a.roleId ?? ''}
                          disabled={reassigning === a.id}
                          onChange={(e) => handleReassign(a, e.target.value)}
                          style={{ ...selectStyle, height: 34, width: 'auto', minWidth: 150 }}
                        >
                          {!a.roleId && <option value="">—</option>}
                          {roles.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <Badge variant={a.status === 'active' ? 'success' : 'neutral'}>
                        {a.status}
                      </Badge>
                    </td>
                    <td style={{ padding: '14px 16px', color: 'var(--neutral-500)' }}>
                      {formatDate(a.lastLoginAt)}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      {a.id !== currentAdmin?.id ? (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                          {canResetAdminLogin ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setResetTarget(a)}
                            >
                              Reset login
                            </Button>
                          ) : null}
                          {a.status === 'active' ? (
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => setPendingAction({ type: 'deactivate', admin: a })}
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => setPendingAction({ type: 'reactivate', admin: a })}
                            >
                              Reactivate
                            </Button>
                          )}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create Admin Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add Admin"
        maxWidth={480}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'var(--blue-100)',
                color: 'var(--blue-700)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 700,
                overflow: 'hidden',
                border: '1px solid var(--neutral-300)',
              }}
            >
              {profilePreview ? (
                <img
                  src={profilePreview}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                'A'
              )}
            </div>
            <label>
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePhotoChange}
                style={{ display: 'none' }}
              />
              <span
                style={{
                  display: 'inline-flex',
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 14px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--neutral-300)',
                  color: 'var(--neutral-800)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Add profile picture
              </span>
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 12 }}>
            <Input
              label="First Name"
              value={form.firstName}
              onChange={(e) => setForm((s) => ({ ...s, firstName: e.target.value }))}
              placeholder="Ada"
            />
            <Input
              label="Last Name"
              value={form.lastName}
              onChange={(e) => setForm((s) => ({ ...s, lastName: e.target.value }))}
              placeholder="Okonkwo"
            />
          </div>
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
            placeholder="ada@payiqapp.com"
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
              Role
            </label>
            <select
              value={form.roleId}
              onChange={(e) => setForm((s) => ({ ...s, roleId: e.target.value }))}
              style={selectStyle}
            >
              {roles.length === 0 && <option value="">No roles available</option>}
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            {(() => {
              const sel = roles.find((r) => r.id === form.roleId);
              return sel?.description ? (
                <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                  {sel.description}
                </span>
              ) : null;
            })()}
          </div>
          <div
            style={{
              padding: 12,
              background: 'var(--blue-100)',
              border: '1px solid var(--blue-300)',
              borderRadius: 'var(--radius-md)',
              fontSize: 13,
              color: 'var(--blue-800)',
            }}
          >
            A temporary password will be auto-generated and must be changed on first login.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Button
              variant="ghost"
              onClick={() => setShowCreateModal(false)}
              disabled={formLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={formLoading}>
              Create Admin
            </Button>
          </div>
        </div>
      </Modal>

      {/* Credentials Modal */}
      <Modal
        isOpen={createdAdmin !== null}
        onClose={() => setCreatedAdmin(null)}
        title="Admin Account Created"
        maxWidth={440}
      >
        {createdAdmin && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
              style={{
                padding: 12,
                background: 'var(--warning-100)',
                border: '1px solid var(--warning-600)',
                borderRadius: 'var(--radius-md)',
                fontSize: 13,
                color: 'var(--warning-900)',
                lineHeight: 1.5,
              }}
            >
              Copy these credentials now and share them securely with the new admin.
              This password will not be shown again.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--neutral-500)', fontWeight: 500 }}>
                  EMAIL
                </span>
                <div
                  style={{
                    padding: '10px 14px',
                    background: 'var(--neutral-200)',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: 'monospace',
                    fontSize: 14,
                    color: 'var(--neutral-800)',
                    userSelect: 'all',
                  }}
                >
                  {createdAdmin.email}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--neutral-500)', fontWeight: 500 }}>
                  TEMPORARY PASSWORD
                </span>
                <div
                  style={{
                    padding: '10px 14px',
                    background: 'var(--neutral-200)',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: 'monospace',
                    fontSize: 14,
                    color: 'var(--neutral-800)',
                    letterSpacing: 1,
                    userSelect: 'all',
                  }}
                >
                  {createdAdmin.temporaryPassword}
                </div>
              </div>
            </div>

            <p style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
              The admin must change this password on first login.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(
                    `Email: ${createdAdmin.email}\nPassword: ${createdAdmin.temporaryPassword}`,
                  );
                  toast.success('Credentials copied to clipboard.');
                }}
              >
                Copy Credentials
              </Button>
              <Button size="sm" onClick={() => setCreatedAdmin(null)}>
                Done
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reset Admin Credentials Modal */}
      <Modal
        isOpen={resetCredentials !== null}
        onClose={() => setResetCredentials(null)}
        title="Admin Login Reset"
        maxWidth={440}
      >
        {resetCredentials ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
              style={{
                padding: 12,
                background: 'var(--warning-100)',
                border: '1px solid var(--warning-600)',
                borderRadius: 'var(--radius-md)',
                fontSize: 13,
                color: 'var(--warning-900)',
                lineHeight: 1.5,
              }}
            >
              Copy these new credentials now and share them securely. This
              temporary password will not be shown again. Existing sessions have
              been revoked.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--neutral-500)', fontWeight: 500 }}>
                  EMAIL
                </span>
                <div
                  style={{
                    padding: '10px 14px',
                    background: 'var(--neutral-200)',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: 'monospace',
                    fontSize: 14,
                    color: 'var(--neutral-800)',
                    userSelect: 'all',
                  }}
                >
                  {resetCredentials.email}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--neutral-500)', fontWeight: 500 }}>
                  TEMPORARY PASSWORD
                </span>
                <div
                  style={{
                    padding: '10px 14px',
                    background: 'var(--neutral-200)',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: 'monospace',
                    fontSize: 14,
                    color: 'var(--neutral-800)',
                    letterSpacing: 1,
                    userSelect: 'all',
                  }}
                >
                  {resetCredentials.temporaryPassword}
                </div>
              </div>
            </div>

            <p style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
              The admin must change this password the next time they log in.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(
                    `Email: ${resetCredentials.email}\nPassword: ${resetCredentials.temporaryPassword}`,
                  );
                  toast.success('Credentials copied to clipboard.');
                }}
              >
                Copy Credentials
              </Button>
              <Button size="sm" onClick={() => setResetCredentials(null)}>
                Done
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Reset Login Confirmation */}
      <Modal
        isOpen={resetTarget !== null}
        onClose={() => (resetLoading ? null : setResetTarget(null))}
        title="Reset admin login?"
        maxWidth={440}
      >
        {resetTarget ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ color: 'var(--neutral-700)', lineHeight: 1.5 }}>
              This generates a fresh temporary password for{' '}
              <strong>{resetTarget.firstName} {resetTarget.lastName}</strong>
              {' '}and forces them to change it on next login. Existing admin
              dashboard sessions for this user will be revoked.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                onClick={() => setResetTarget(null)}
                disabled={resetLoading}
              >
                Cancel
              </Button>
              <Button onClick={handleResetLogin} disabled={resetLoading}>
                {resetLoading ? 'Resetting…' : 'Generate temp password'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Confirm Deactivate / Reactivate */}
      <ConfirmDialog
        isOpen={pendingAction !== null}
        title={
          pendingAction?.type === 'deactivate'
            ? 'Deactivate admin account?'
            : 'Reactivate admin account?'
        }
        message={
          pendingAction?.type === 'deactivate'
            ? `${pendingAction?.admin.email} will lose all access immediately. They can be reactivated at any time.`
            : `${pendingAction?.admin.email} will regain access with their existing role and permissions.`
        }
        confirmLabel={
          pendingAction?.type === 'deactivate' ? 'Deactivate' : 'Reactivate'
        }
        variant={pendingAction?.type === 'deactivate' ? 'danger' : 'primary'}
        loading={actionLoading}
        onConfirm={executeAction}
        onCancel={() => setPendingAction(null)}
      />
    </>
  );
};
